import { ItemLink } from "../ItemLink";
import { ItemStatsRecipe } from "./ItemStatsRecipe";
import { parseDescription } from "../item-parsers";
import { HeaderBullet } from "@ugg/shared/components/common/HeaderBullet";
import type { Item } from "@outplayed/riot-assets";

// const tagRegex =
//   /magicDamage|physicalDamage|attackDamage|trueDamage|truedamage|healing|speed|scaleAP|scaleAD|scaleHealth|scaleMana|scaleArmor|scaleMR|scaleLevel|lifeSteal|status|OnHit|shield|active|rarityGeneric|rarityLegendary|keywordMajor|keywordStealth/g;

const tagRegex =
  /<magicDamage>|<physicalDamage>|<attackDamage>|<trueDamage>|<truedamage>|<healing>|<speed>|<scaleAP>|<scaleAD>|<scaleLethality>|<scaleHealth>|<scaleMana>|<scaleArmor>|<scaleMR>|<scaleLevel>|<lifeSteal>|<OnHit>|<goldGain>|<status>|<shield>|<active>|<unique>|<rarityGeneric>|<rarityLegendary>|<keywordMajor>|<keywordStealth>/g;

const tagMap: Record<string, string> = {
  "<magicDamage>": "magic-damage",
  "<physicalDamage>": "physical-damage",
  "<attackDamage>": "physical-damage",
  "<trueDamage>": "true-damage",
  "<truedamage>": "true-damage",
  "<healing>": "healing",
  "<speed>": "speed", // white
  "<scaleAP>": "magic-damage",
  "<scaleAD>": "physical-damage",
  "<scaleLethality>": "status",
  "<scaleHealth>": "health",
  "<scaleMana>": "mana",
  "<scaleArmor>": "armor",
  "<scaleMR>": "magic-resistance",
  "<scaleLevel>": "status",
  "<lifeSteal>": "status",
  "<goldGain>": "status",
  "<status>": "status", // white
  "<OnHit>": "status",
  "<active>": "status",
  "<unique>": "status",
  "<shield>": "italic",
  "<rarityGeneric>": "status",
  "<rarityLegendary>": "status",
  "<keywordMajor>": "italic",
  "<keywordStealth>": "italic",
};

interface ItemDetailsProps {
  itemId: number;
  itemData: Item;
  isMythic?: boolean;
}

export function ItemDetails(props: ItemDetailsProps) {
  const { itemId, itemData, isMythic } = props;

  if (!itemId || !itemData) return null;

  const itemProps = {
    itemId: itemId,
    className: isMythic ? "items-pic-large items-border-mythic-small" : "items-pic-large items-border-normal-small",
  };

  // TODO ask about certain names (void pen, hydra, wardstone)
  const checkLimitation = (itemData: Item, itemId: string) => {
    if (!itemData || !itemId) return null;
    // console.log(itemData);

    const desc = itemData.description;
    const tags = itemData.tags ? itemData.tags : [];

    const jungleItems = ["1035", "1039"];
    const supportItems = ["3858", "3862", "3850", "3854", "3851", "3863", "3855", "3859", "3864", "3860", "3857", "3853"];
    const critModifier = ["3124", "3031", "6677"];
    const armorPen = ["3035", "3036", "6694"];
    const mythicComponent = ["6660", "6029", "4635", "3802", "6670"];
    const percentMagicPen = ["3135", "4630"];
    const elixers = ["2138", "2139", "2140"];
    const potions = ["2033", "2003", "2031"];
    const sightStones = ["4638", "4643"];
    // tags: [ 'CriticalStrike', 'AttackSpeed', 'OnHit' ]

    const limitations = [];

    if (desc.includes("<rarityMythic>")) {
      limitations.push("Mythic");
    }
    if (tags.includes("Boots")) {
      limitations.push("Boots");
    }
    if (critModifier.includes(itemId)) {
      limitations.push("Crit Modifier");
    }
    if (jungleItems.includes(itemId) || supportItems.includes(itemId)) {
      limitations.push("Jungle or Support");
    }
    if (desc.includes("Mana Charge:") || desc.includes("Awe:")) {
      limitations.push("Mana Charge");
    }
    if (desc.includes("Lifeline:")) {
      limitations.push("Lifeline");
    }
    if (desc.includes("Quicksilver:")) {
      limitations.push("Quicksilver");
    }
    if (armorPen.includes(itemId)) {
      limitations.push("Armor Penetration");
    }
    if (mythicComponent.includes(itemId)) {
      limitations.push("Mythic Component");
    }
    if (percentMagicPen.includes(itemId)) {
      limitations.push("Percent Magic Pen");
    }
    if (desc.includes("Cleave:")) {
      limitations.push("Hydra");
    }
    // need to check if you can buy multple dark seals
    if (desc.includes("Glory:")) {
      limitations.push("Glory");
    }
    if (elixers.includes(itemId)) {
      limitations.push("Elixer");
    }
    // TODO this should be worded differently
    if (potions.includes(itemId)) {
      limitations.push("Potion");
    }
    if (tags.includes("Trinket")) {
      limitations.push("Trinket");
    }
    // name check??
    if (sightStones.includes(itemId)) {
      limitations.push("Wardstone");
    }

    if (limitations.length > 0) {
      if (limitations.length === 1) {
        return (
          <div className="limitation-text">
            Limited to 1 <strong>{limitations[0]}</strong> item.
          </div>
        );
      }
      if (limitations.length === 2) {
        return (
          <div className="limitation-text">
            Limited to 1 <strong>{limitations[0]}</strong> and <strong>{limitations[1]}</strong> item.
          </div>
        );
      }
    }

    return null;
  };

  const limitations = checkLimitation(itemData, String(itemId));

  // TODO emberknife stats don't show
  // TODO certain support items have bugged descriptions (with 0 wards)

  const parseStatLine = (stat: string) => {
    const regex =
      /[0-9]+%|[0-9]+|Attack Damage|Ability Power|Health|Magic Resist|Attack Speed|Move Speed|Ability Haste|Armor Penetration|Lethality|Life Steal|Omnivamp|Tenacity|Critical Strike Chance|Magic Penetration|Gold Per 10 Seconds|Base Health Regen|Base Mana Regen|Heal and Shield Power|Mana|Armor/g;
    const statSplit = stat.match(regex);

    return statSplit;
  };

  const parseActive = (active: string) => {
    if (!active.includes(":")) return null;

    const specialClasses: string[] = [];
    const parsedActive = active.replace("Active - ", "");
    const activeSplit = parsedActive.split(":");
    const activeRegex = /\/|<OnHit>|<\/OnHit>|<scaleLevel>|<\/scaleLevel>/g;
    const activeDesc = activeSplit[1].replace(activeRegex, "");

    let activeText = [activeDesc];
    const allTags = activeText[0].match(tagRegex) || [];
    // removing any odd tags that may have slipped inside allTags
    for (let i = 0; i < allTags.length; i++) {
      if (i % 2 === 0) {
        if (allTags[i + 1] !== allTags[i]) {
          allTags.splice(i, 1);
        }
      }
      if (i % 2 === 1) {
        if (allTags[i - 1] !== allTags[i]) {
          allTags.splice(i, 1);
        }
      }
    }

    for (let i = 0; i < allTags.length; i += 2) {
      // this should always be true
      if (allTags[i + 1] === allTags[i]) {
        // index SHOULD be the same as i
        const index = activeText.length - 1;
        if (activeText[index].includes(allTags[i])) {
          // splitting the text between keyword (allTags[i])
          const split = activeText[index].split(allTags[i]);

          // firstText is the first 2 strings of the split
          const firstText = split.splice(0, 2);

          // adding the remaining block of text at the end
          const remainingText = split.join(allTags[i]);
          firstText.push(remainingText);

          // stiching them back together
          const firstPart = activeText.slice(0, index);
          activeText = [...firstPart, ...firstText];

          specialClasses.push(tagMap[allTags[i]]);
        }
      } else i -= 1; // meant to skip certain keywords that end up in allTags (ie: active)
    }

    return (
      <div key={active} className="passive-line">
        <strong>Unique</strong>&nbsp;-&nbsp;
        <span className="passive-title">{activeSplit[0]}</span>:
        {specialClasses.length > 0 &&
          activeText.map((text, i) => {
            if (i % 2 === 1) {
              return (
                <span key={`${text}-${i}`} className={specialClasses[(i - 1) / 2]}>
                  {text}
                </span>
              );
            } else return <span key={`${text}-${i}`}>{text}</span>;
          })}
        {specialClasses.length === 0 && activeText}
      </div>
    );
  };

  const parsePassive = (passive: string) => {
    if (!passive?.includes(":")) return null;

    const specialClasses: string[] = [];
    const passiveSplit = passive.split(":");
    const passiveRegex = /\//g;
    const passiveDesc = passiveSplit[1].replace(passiveRegex, "");

    let passiveText = [passiveDesc];
    // console.log(passiveText);
    const allTags = passiveText[0].match(tagRegex) || [];

    // removing any odd tags that may have slipped inside allTags
    for (let i = 0; i < allTags.length; i++) {
      if (i % 2 === 0) {
        if (allTags[i + 1] !== allTags[i]) {
          allTags.splice(i, 1);
        }
      }
      if (i % 2 === 1) {
        if (allTags[i - 1] !== allTags[i]) {
          allTags.splice(i, 1);
        }
      }
    }

    for (let i = 0; i < allTags.length; i += 2) {
      // this should always be true
      if (allTags[i + 1] === allTags[i]) {
        // index SHOULD be the same as i
        const index = passiveText.length - 1;
        if (passiveText[index].includes(allTags[i])) {
          // splitting the text between keyword (allTags[i])
          const split = passiveText[index].split(allTags[i]);

          // firstText is the first 2 strings of the split
          const firstText = split.splice(0, 2);

          // adding the remaining block of text at the end
          const remainingText = split.join(allTags[i]);
          firstText.push(remainingText);

          // stiching them back together
          const firstPart = passiveText.slice(0, index);
          passiveText = [...firstPart, ...firstText];

          specialClasses.push(tagMap[allTags[i]]);
        }
      } else i -= 1; // meant to skip certain keywords that end up in allTags (ie: active)
    }

    return (
      <div key={passive} className="passive-line">
        <strong>Unique</strong>&nbsp;-&nbsp;
        <span className="passive-title">{passiveSplit[0]}</span>:
        {specialClasses.length > 0 &&
          passiveText.map((text, i) => {
            if (i % 2 === 1) {
              return (
                <span key={`${text}-${i}`} className={specialClasses[(i - 1) / 2]}>
                  {text}
                </span>
              );
            } else return <span key={`${text}-${i}`}>{text}</span>;
          })}
        {specialClasses.length === 0 && passiveText}
      </div>
    );
  };

  const parseMythicPassive = (mythicPassive: string) => {
    const regex = /<br>|<passive>|<\/passive>/gi;
    const parsedMythic = mythicPassive.replace(regex, "");
    const mythicSplit = parsedMythic.split(":");

    return (
      <div className="passive-line">
        <strong>{mythicSplit[0]}</strong>: {mythicSplit[1]}
      </div>
    );
  };

  const { stats, mythicPassive, actives, passives } = parseDescription(itemData.description);

  return (
    <div className="items-details">
      <div className="items-details-container">
        <div className="items-details-header">
          <HeaderBullet className="mb-[18px]">{itemData.name}</HeaderBullet>
          <div className="items-details-pic">
            <ItemLink {...itemProps} />
          </div>
        </div>
        {stats?.length > 0 && stats[0] && (
          <div className="section items-details-section">
            <HeaderBullet className="mb-[18px]">Stats</HeaderBullet>
            <div className="stats-list">
              {stats.map((stat) => {
                const [number, statName] = parseStatLine(stat) || [];
                return (
                  <div key={stat} className="stat-line">
                    +{number}&nbsp;<strong>{statName}</strong>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {actives?.length > 0 && actives[0] && (
          <div className="items-details-section">
            <HeaderBullet className="mb-[18px]">Active</HeaderBullet>
            <div className="passives-list">{actives?.map((active) => parseActive(active))}</div>
          </div>
        )}
        {(passives?.length > 0 || mythicPassive) && (
          <div className="items-details-section">
            <HeaderBullet className="mb-[18px]">Passive</HeaderBullet>
            <div className="passives-list">
              {passives?.map((passive) => parsePassive(passive))}
              {mythicPassive && parseMythicPassive(mythicPassive)}
            </div>
          </div>
        )}
        {limitations && (
          <div className="items-details-section">
            <HeaderBullet className="mb-[18px]">Limitations</HeaderBullet>
            {limitations}
          </div>
        )}

        <ItemStatsRecipe itemId={itemId} itemData={itemData} />
      </div>
    </div>
  );
}
